/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  MoreHorizontal,
  Mail,
  Phone,
  Target,
  CheckCircle,
  XCircle,
  Percent,
  Clock,
  CalendarIcon,
  AlertCircle,
} from "lucide-react";
import { CSSTransition } from "react-transition-group";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import EditPopup from "../Components/EditPopup";
import DeletePopup from "../Components/DeletePopup";
import Popup from "../Components/Popup";
import ManagerTasks from "../Components/ManagerTasks";
import PeriodContextSelector from "../Components/PeriodContextSelector";
import QuickTasks from "../Components/QuickTasks";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";

const StatsCard = ({ title, value, icon: Icon, color = "blue" }) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <div className={`p-4 bg-${color}-100 rounded-full`}>
          <Icon className={`text-${color}-600`} size={24} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const ReportDialog = ({ open, onOpenChange, managerId }) => {
  const [date, setDate] = useState(undefined);
  const [options, setOptions] = useState({
    kpis: true,
    tasks: false,
    carePlans: false,
    clientFeedbacks: false,
  });
  const [loading, setLoading] = useState(false);
  const apiUrl = import.meta.env.VITE_API_URL;
  const token = JSON.parse(localStorage.getItem("user"))?.access;

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);

      const details = [];
      for (const key in options) {
        if (options[key]) {
          details.push(key.replace(" ", "-"));
        }
      }
      if (details.length === 0) {
        toast.error("Select at least one report content");
        setLoading(false);
        return;
      }

      if (!date?.from || !date?.to) {
        toast.error("Select date range");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${apiUrl}/api/v1/tracker/${managerId}/insights/`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              date_range: date,
              details,
            }),
          }
        );

        if (!response.ok) throw new Error("Failed to generate report");
        toast.success("Report generated successfully");
      } catch (error) {
        toast.error(error instanceof Error ? error.message : error);
      } finally {
        setLoading(false);
        onOpenChange(false);
      }
    },
    [date, managerId, onOpenChange, options, token, apiUrl]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Download Report</DialogTitle>
          <DialogDescription>
            Select date range and report contents
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col space-y-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Date Range</label>
              <Popover modal>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      "border rounded-md px-3 py-2 text-sm",
                      !date?.from && "text-gray-400"
                    )}
                  >
                    {date?.from ? (
                      date?.to ? (
                        <>
                          {format(date.from, "LLL dd, y")} -{" "}
                          {format(date.to, "LLL dd, y")}
                        </>
                      ) : (
                        format(date.from, "LLL dd, y")
                      )
                    ) : (
                      "Select date range"
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    defaultMonth={date?.from}
                    selected={date}
                    onSelect={(newDate) => {
                      console.log("newDate", newDate);
                      setDate(newDate);
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-4">
              <label className="text-sm font-medium">Include in Report</label>
              <div className="space-y-3">
                {[
                  { id: "kpis", label: "KPIs" },
                  { id: "tasks", label: "Tasks" },
                  { id: "carePlans", label: "Care Plans" },
                  { id: "clientFeedbacks", label: "Client Feedbacks" },
                ].map(({ id, label }) => (
                  <div key={id} className="flex items-center space-x-2">
                    <Checkbox
                      id={id}
                      checked={options[id]}
                      onCheckedChange={(checked) =>
                        setOptions((prev) => ({ ...prev, [id]: checked }))
                      }
                    />
                    <label
                      htmlFor={id}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button
              type="submit"
              disabled={loading}
              className={cn(
                "bg-btn text-white px-4 py-2 rounded-md",
                "hover:bg-btn/80 transition-colors",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {loading ? "Generating..." : "Generate Report"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ManagerProfile = ({ manager, onEdit, onDelete }) => {
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  return (
    <>
      <ReportDialog
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        managerId={manager.id}
      />
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start gap-6">
            {manager.profile_picture ? (
              <img
                src={manager.profile_picture.replace("l", "")}
                alt="Profile"
                className="flex-shrink-0 w-20 h-20 rounded-full flex items-center justify-center"
              />
            ) : (
              <div className="flex-shrink-0 w-20 h-20  bg-gray-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-gray-600">
                  {manager.first_name[0]}
                  {manager.last_name[0]}
                </span>
              </div>
            )}
            <div className="flex-grow">
              <h2 className="text-2xl font-bold mb-2">
                {manager.first_name} {manager.last_name}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="flex items-center gap-3 text-gray-600">
                  <Mail size={18} className="flex-shrink-0" />
                  <span className="truncate">{manager.email}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <Phone size={18} className="flex-shrink-0" />
                  <span>{manager.phone}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <CalendarIcon className="flex-shrink-0" size={18} />
                  <span>
                    Joined {new Date(manager.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <MoreHorizontal size={20} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit} className="cursor-pointer">
                  Edit Manager
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer" asChild>
                  <Link to={`/managers/${manager.id}/sessions`}>
                    View Sessions
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setReportDialogOpen(true)}
                  className="cursor-pointer"
                >
                  Download Report
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onDelete}
                  className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                >
                  Delete Manager
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

const TabButton = ({ active, children, onClick }) => (
  <button
    onClick={onClick}
    className={`px-6 py-3 text-sm font-medium rounded-lg transition-all
      ${
        active
          ? "bg-btn text-white shadow-lg"
          : "text-gray-600 hover:bg-gray-100"
      }`}
  >
    {children}
  </button>
);

const PerformanceMetrics = ({ quickStats }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
    <StatsCard
      title="Total KPIs"
      value={quickStats.totalKpis}
      icon={Target}
      color="red"
    />
    <StatsCard
      title="Completed"
      value={quickStats.completed}
      icon={CheckCircle}
      color="green"
    />
    <StatsCard
      title="Pending"
      value={quickStats.undone}
      icon={Clock}
      color="yellow"
    />
    <StatsCard
      title="Completion Rate"
      value={`${quickStats.completionRate || 0}%`}
      icon={Percent}
    />
  </div>
);

const KPITable = ({ kpis }) => {
  const getPeriodIcon = (period) => {
    switch (period.toUpperCase()) {
      case "WEEKLY":
        return <Clock className="text-blue-500" size={20} />;
      case "MONTHLY":
        return <Target className="text-green-500" size={20} />;
      default:
        return <AlertCircle className="text-yellow-500" size={20} />;
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return "bg-green-500";
    if (percentage >= 50) return "bg-yellow-500";
    return "bg-red-500";
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>KPI Details</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {kpis.map((kpi) => (
            <div key={kpi.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {getPeriodIcon(kpi.period)}
                  <h3 className="text-lg font-semibold">{kpi.name}</h3>
                </div>
                <span className="px-3 py-1 text-sm rounded-full bg-gray-100">
                  {kpi.period}
                </span>
              </div>

              <p className="text-gray-600 text-sm mb-4">{kpi.description}</p>

              <div className="space-y-4">
                {kpi.targets.map((target) => (
                  <div key={target.id} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{target.name}</span>
                      <span className="text-sm text-gray-500">
                        Target: {target.total_expected}
                        {target.is_inverted ? " (Lower is better)" : ""}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`${getProgressColor(
                          kpi.percentage_completed
                        )} h-2 rounded-full transition-all duration-500`}
                        style={{ width: `${kpi.percentage_completed}%` }}
                      />
                    </div>
                    <div className="flex justify-between items-center text-sm text-gray-600">
                      <span>{target.description}</span>
                      <span className="font-medium">
                        {kpi.percentage_completed.toFixed(1)}% Complete
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const TaskSection = () => (
  <Card>
    <CardHeader>
      <CardTitle>Recent Tasks</CardTitle>
    </CardHeader>
    <CardContent>
      <QuickTasks />
      <ManagerTasks />
    </CardContent>
  </Card>
);

const ManagerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("KPI");
  const [popup, setPopup] = useState(false);
  const [edit, setEdit] = useState(false);
  const [erase, setErase] = useState(false);

  const apiUrl = import.meta.env.VITE_API_URL;
  const token = JSON.parse(localStorage.getItem("user"))?.access;

  const [manager, setManager] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickStats, setQuickStats] = useState({
    totalKpis: 0,
    completed: 0,
    undone: 0,
    completionRate: 0,
  });
  const [managerKpis, setManagerKpis] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const managerRes = await fetch(
          `${apiUrl}/api/v1/auth/users/?user_type=MANAGER`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!managerRes.ok) throw new Error("Failed to fetch managers");
        const managersData = await managerRes.json();
        const selectedManager = managersData.data.find(
          (m) => m.user_type === "MANAGER" && m.id === id
        );
        if (!selectedManager) {
          toast.error("Manager not found");
          setManager(null);
          return;
        } else {
          setManager(selectedManager);
        }

        const statsRes = await fetch(
          `${apiUrl}/api/v1/tracker/admin/kpis/?assignee=${selectedManager.manager_profile}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!statsRes.ok) throw new Error("Failed to fetch manager stats");
        const statsData = await statsRes.json();
        const managerKpis = statsData.data.filter((kpi) => kpi?.user_id === id);
        const completedKpis = managerKpis.filter(
          (kpi) => kpi.percentage_completed === 100
        );

        setQuickStats({
          totalKpis: managerKpis.length,
          completed: completedKpis.length,
          undone: managerKpis.length - completedKpis.length,
          completionRate: (
            (completedKpis.length / managerKpis.length) *
            100
          ).toFixed(1),
        });
        setManagerKpis(managerKpis);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl, token, id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="w-12 h-12 border-4 border-btn border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!manager) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
        <XCircle className="text-red-500 mb-4" size={48} />
        <h2 className="text-xl font-semibold text-gray-800">
          Manager Not Found
        </h2>
        <p className="text-gray-600 mt-2">
          The requested manager could not be found.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-btn text-white rounded-lg hover:bg-btn/80 transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 md:ml-[350px] min-h-screen">
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">
              Manager Performance
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <PeriodContextSelector />
          </div>
        </div>

        {/* Profile Section */}
        <ManagerProfile
          manager={manager}
          onEdit={() => setEdit(true)}
          onDelete={() => setErase(true)}
        />

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <TabButton
            active={activeTab === "KPI"}
            onClick={() => setActiveTab("KPI")}
          >
            KPIs
          </TabButton>
          <TabButton
            active={activeTab === "TASKS"}
            onClick={() => setActiveTab("TASKS")}
          >
            Tasks
          </TabButton>
        </div>

        {/* Content */}
        <div className="space-y-6 pb-24">
          {activeTab === "KPI" ? (
            <>
              <PerformanceMetrics quickStats={quickStats} />
              <KPITable kpis={managerKpis} />
              {/* Add KPI charts and detailed metrics here */}
            </>
          ) : (
            <TaskSection />
          )}
        </div>
      </div>

      {/* Popups */}
      <EditPopup
        selectedItem={manager}
        setEdit={setEdit}
        styles={{
          enter: "transform translate-y-full opacity-0",
          enterActive:
            "transform -translate-y-0 opacity-100 transition-all duration-500 ease-in-out",
          exitActive:
            "transform translate-y-full opacity-0 transition-all duration-500 ease-in-out",
        }}
        edit={edit}
      />

      <Popup
        userId={id}
        setEdit={setEdit}
        setErase={setErase}
        styles={{
          enter: "transform translate-y-full opacity-0",
          enterActive:
            "transform -translate-y-0 opacity-100 transition-all duration-500 ease-in-out",
          exitActive:
            "transform translate-y-full opacity-0 transition-all duration-500 ease-in-out",
        }}
        popup={popup}
        setPopup={setPopup}
      />

      <DeletePopup
        CSSTransition={CSSTransition}
        apiUrl={apiUrl}
        token={token}
        user_id={id}
        erase={erase}
        setErase={setErase}
        styles={{
          enter: "transform translate-y-full opacity-0",
          enterActive:
            "transform -translate-y-0 opacity-100 transition-all duration-500 ease-in-out",
          exitActive:
            "transform translate-y-full opacity-0 transition-all duration-500 ease-in-out",
        }}
      />
    </div>
  );
};

export default ManagerDetails;
